import ToolTip from "components/General/ToolTip"
import { formatToNumericalDate } from "helpers/dateHelper"
import { Table, Button } from "react-bootstrap"
import { useNavigate } from "react-router"
import lessons from "shared/lessons"
import { AssignmentResponse, StudentResponse } from "shared/types/teacherTypes"
import EmptyTablePreview from "../../General/EmptyTablePreview"
import { TeacherGradesState } from 'components/Lessons/Grading/GradeList/TeacherGrades'
import { CurriculumTopic } from "shared/types/curriculumTypes"
import books from "shared/routes/curriculum/books"
import { StudentAssignmentGrade } from "./GradeBook"
import { ExclamationOctagon, ExclamationOctagonFill } from "react-bootstrap-icons"

interface GradeBookTableProps {
    assignments: AssignmentResponse[]
    students: StudentResponse[]
    studentGrades: { [key: string]: StudentAssignmentGrade[] }
    startIndex: number
    count: number
    calculateClassAverage: (assignment: AssignmentResponse) => string
    calculateStudentClassGrade: (student: StudentResponse) => string
    isUnweightedAssignment: (assignmentId: string) => boolean
}

export default function GradeBookTable({ assignments, students, studentGrades, startIndex, count, calculateClassAverage, calculateStudentClassGrade, isUnweightedAssignment } : GradeBookTableProps) {
    const navigate = useNavigate()

    const handleAssignmentClick = async (assignment: AssignmentResponse) => {
        if (assignment.lessonVariety === "Guided" || assignment.lessonVariety === "Topic") {
            let topic: CurriculumTopic
            if(assignment.lessonId) {
                var lesson = await lessons.findById(assignment.lessonId)
            }

            if (assignment.topicId) {
                topic = await books.getTopic({ topicId: assignment.topicId })
            }


            const TeacherGradesState: TeacherGradesState = {
                assignment: assignment,
                lesson: lesson,
                topic: topic,
            }
            navigate("/dashboard/teacher/assignments/grading", {
                state: TeacherGradesState
            })
        } else if (assignment.lessonVariety === "Pure Inquiry") {
            const TeacherGradesState: TeacherGradesState = {
                assignment: assignment
            }
            navigate("/dashboard/teacher/assignments/grading", {
                state: TeacherGradesState
            })
        }
    }

    const onClickAddAssignments = () => {
        // reset selectedGradeInit and initStandardId filters so that grade level and standard id from current class would be pre-selected in lesson search page
        localStorage.removeItem("selectedGrades")
        localStorage.removeItem("selectedStandardId")
        navigate("/dashboard/teacher/assignments/search")
    }


    return (
        <>
            {assignments.length !== 0 ? <Table responsive className='mb-5 table-striped' style={{ verticalAlign: "middle", position: "relative", overflowY: "auto" }}>
                <thead className="sticky-top" style={{ verticalAlign: "middle" }}>
                    <tr>
                        <th className="border-end"></th>
                        {assignments.map((assignment, index) => {
                            const unweightedAssignment = isUnweightedAssignment(assignment.id)
                            if (index >= startIndex && index < startIndex + count) {
                                return (
                                    <th
                                        key={assignment.id}
                                        className="text-center align-middle border-end"
                                        onClick={() => handleAssignmentClick(assignment)}
                                        role="button"
                                    >
                                        <div>
                                            <p className='link-primary link-offset-2 link-underline-opacity-25 link-underline-opacity-100-hover'>
                                                {unweightedAssignment && <ToolTip title="assignment has no weights"><ExclamationOctagonFill size={25} className="text-danger me-1" /></ToolTip>}
                                                {assignment.title}
                                            </p>
                                        </div>
                                    </th>
                                )
                            } else {
                                return null
                            }
                        })}
                        <th className="text-center py-3">Student Class Grade</th>
                    </tr>
                    <tr className='border-bottom'>
                        <td className='text-center py-3 px-4' style={{ fontWeight: "bold" }}>Due Date</td>
                        {assignments.map((assignment, index) => {
                            if (index >= startIndex && index < startIndex + count) {
                                return (
                                    <td key={assignment.id} className="text-center py-auto">
                                        {assignment.dueDate ? formatToNumericalDate(assignment.dueDate) : "N/A"}
                                    </td>
                                )
                            } else {
                                return null
                            }
                        })}
                        <th></th>
                    </tr>
                </thead>
                <tbody style={{ verticalAlign: "middle" }}>
                    {students.map(student => (
                        <tr key={student.uuid}>
                            <td className={`text-center py-3 border-end`}>{student.name}</td>
                            {studentGrades[student.uuid].map((assignment, index) => {
                                if (index >= startIndex && index < startIndex + count) {
                                    if (assignment.late) {
                                        return (
                                            <ToolTip key={"tt" +student.uuid + assignment.assignmentId} title={assignment.late && "Late assignment"}>
                                                <td key={student.uuid + assignment.assignmentId} style={{ backgroundColor: "#fec1c1" }} className={`text-center py-3`}>
                                                    {assignment.grade && `${assignment.grade}%`}
                                                </td>
                                            </ToolTip>
                                        )
                                    } else if (isUnweightedAssignment(assignment.assignmentId)) {
                                        return (
                                            <ToolTip key={"tt" +student.uuid + assignment.assignmentId} title={"No weights"}>
                                                <td key={student.uuid + assignment.assignmentId} className={`text-center`}>
                                                    <ExclamationOctagon size={20} className="text-danger" />
                                                </td>
                                            </ToolTip>
                                        )
                                    } 
                                    else {
                                        return (
                                            <td key={student.uuid + assignment.assignmentId} className={`text-center py-3`} >
                                                {assignment.grade && `${assignment.grade}%`}
                                            </td>
                                        )
                                    }
                                } else {
                                    return null
                                }
                            })}
                            <td className="text-center py-3" style={{ fontWeight: "bold" }}>{calculateStudentClassGrade(student)}</td>
                        </tr>
                    ))}
                    <tr className='border-top'>
                        <td className="text-center py-3 px-4" style={{ fontWeight: "bold" }}>Class Average</td>
                        {assignments.map((assignment, index) => {
                            if (index >= startIndex && index < startIndex + count) {
                                return (
                                    <td key={"average" + assignment.id} className="text-center" style={{ fontWeight: "bold" }}>
                                        {calculateClassAverage(assignment)}
                                    </td>
                                )
                            } else {
                                return null
                            }
                        })}
                        <td></td>
                    </tr>
                </tbody>
            </Table>
                :
                <EmptyTablePreview
                    title="Create Assignments"
                    description="When you add an assignment, it will be displayed at the top of the gradebook."
                >
                    <Button
                        variant="theme"
                        onClick={onClickAddAssignments}
                        data-testid="empty-add-assignment"
                    >
                        + Add Assignments
                    </Button>
                </EmptyTablePreview>

            }
        </>
    )
}
